import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { BlurredContainer } from '@/features/insights/components/blurred-container';
import TendencyIndicator, {
  TendencyType,
} from '@/shared/components/tendency-indicator';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { getKpiUnitTranslationPlural } from '@/shared/hooks/use-kpi-unit-translation';
import { KpiUnit } from '@/shared/types/kpi';
import { formatNumberWithDots } from '@/shared/utils/format';

type ForecastTimePeriod = 'monthly' | 'quarterly' | 'yearly';

const getTimePeriodLabel = (
  timePeriod: ForecastTimePeriod,
  t: TFunction<'forecast', 'ForecastTimeCard'>,
) => {
  switch (timePeriod) {
    case 'monthly':
      return t('monthEnd');
    case 'quarterly':
      return t('quarterEnd');
    case 'yearly':
      return t('yearEnd');
  }
};

export const ForecastTimeCard = ({
  timePeriod,
  value,
  tendency,
  kpiUnit,
}: {
  timePeriod: ForecastTimePeriod;
  value: number;
  tendency: TendencyType;
  kpiUnit: string | KpiUnit;
}) => {
  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });
  const { t: tForecast } = useTranslation('forecast', {
    keyPrefix: 'ForecastTimeCard',
  });

  return (
    <Card
      className="max-w-none flex-1 border-none bg-dark-container"
      variant={'information'}
    >
      <CardHeader className="flex flex-row justify-between">
        <TendencyIndicator tendency={tendency} />
        <CardTitle>{getTimePeriodLabel(timePeriod, tForecast)}</CardTitle>
      </CardHeader>
      <CardContent>
        <CardTitle className="text-4xl leading-none">
          {formatNumberWithDots(value)}
        </CardTitle>
        <CardDescription className="text-base">
          {getKpiUnitTranslationPlural(tKpiTypes, kpiUnit)}
        </CardDescription>
      </CardContent>
    </Card>
  );
};

export const ForecastTimeCardSkeleton = () => {
  return (
    <BlurredContainer isBlurred={true} className="flex-1">
      <Card
        className="pointer-events-none h-[184px] max-w-none border-none bg-dark-container"
        variant={'information'}
      />
    </BlurredContainer>
  );
};
